import { useMemo } from "react";
import { useTours } from "@/lib/siteData";
import { useAdminBookings, useAdminSlots, useAdminPayments, occupies, money, spDay, todaySP, fmtDateTime, bookingDay } from "@/lib/booking";
import { Card, SubTitle } from "./adminUI";

function Stat({ label, value, testid }) {
    return <Card className="text-center"><p className="font-display text-3xl font-black text-neon-cyan" data-testid={testid}>{value}</p>
        <p className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-dim">{label}</p></Card>;
}

export default function BookingStats() {
    const bData = useAdminBookings().data;
    const sData = useAdminSlots().data;
    const pData = useAdminPayments().data;
    const tours = Object.fromEntries(useTours(true).map((t) => [t.id, t.title]));
    const s = useMemo(() => {
        const bookings = bData || [];
        const slots = sData || [];
        const payments = pData || [];
        const slotsById = Object.fromEntries(slots.map((x) => [x.id, x]));
        const today = todaySP();
        const week = spDay(Date.now() + 7 * 864e5);
        const active = bookings.filter((b) => !["cancelled", "expired", "refunded"].includes(b.status));
        const upcoming = active.filter((b) => b.slot_id && slotsById[b.slot_id] && new Date(slotsById[b.slot_id].starts_at) > new Date())
            .sort((a, b) => slotsById[a.slot_id].starts_at.localeCompare(slotsById[b.slot_id].starts_at));
        const weekSlots = slots.filter((x) => x.active && spDay(x.starts_at) >= today && spDay(x.starts_at) <= week);
        const cap = weekSlots.reduce((n, x) => n + x.capacity, 0);
        const occ = bookings.filter((b) => occupies(b) && weekSlots.some((x) => x.id === b.slot_id)).reduce((n, b) => n + b.guests, 0);
        const top = Object.entries(active.reduce((a, b) => ({ ...a, [b.tour_id]: (a[b.tour_id] || 0) + 1 }), {})).sort((a, b) => b[1] - a[1]).slice(0, 5);
        return {
            today: active.filter((b) => bookingDay(b, slotsById) === today).length,
            upcoming: upcoming.slice(0, 5).map((b) => ({ ...b, when: slotsById[b.slot_id].starts_at })),
            revenue: payments.reduce((n, p) => n + (p.kind === "payment" ? 1 : -1) * Number(p.amount), 0),
            pending: bookings.filter((b) => occupies(b) && Number(b.total) > Number(b.paid_amount)).length,
            occupancy: cap ? Math.round((occ / cap) * 100) : 0,
            top,
        };
    }, [bData, sData, pData]);

    return (
        <div className="space-y-4" data-testid="booking-stats">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <Stat label="Reservas do dia" value={s.today} testid="stats-today" />
                <Stat label="Receitas registradas" value={money(s.revenue)} testid="stats-revenue" />
                <Stat label="Pagamentos pendentes" value={s.pending} testid="stats-pending" />
                <Stat label="Ocupação (7 dias)" value={`${s.occupancy}%`} testid="stats-occupancy" />
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
                <Card className="space-y-2"><SubTitle>Próximos agendamentos</SubTitle>
                    {s.upcoming.map((b) => <p key={b.id} className="text-sm text-dim">{fmtDateTime(b.when)} · {b.customer_name} · {b.guests} pessoa(s) · {tours[b.tour_id] || ""}</p>)}
                    {!s.upcoming.length && <p className="text-xs text-dim">Nenhum agendamento futuro.</p>}</Card>
                <Card className="space-y-2"><SubTitle>Mais procurados</SubTitle>
                    {s.top.map(([id, n]) => <p key={id} className="text-sm text-dim">{tours[id] || "Experiência"} · {n} reserva(s)</p>)}
                    {!s.top.length && <p className="text-xs text-dim">Sem dados ainda.</p>}</Card>
            </div>
        </div>
    );
}
